import React, { useState } from "react";
import Button from "../../../components/ui/Button";
import ConfirmModal from "../../../components/ui/ConfirmModal";

/**
 * Context-aware action buttons for order lifecycle transitions.
 *
 * Props:
 *   order    — the full order object (must include status, user_role)
 *   onAction — callback: (actionType, data?) => void
 *   loading  — optional boolean to disable all buttons during API call
 */
const OrderActions = ({ order, onAction, loading = false }) => {
    const [confirmAction, setConfirmAction] = useState(null);

    if (!order) return null;

    const status = order.status === "fulfilled" ? "completed" : order.status;
    const { user_role } = order;
    const isSeller = user_role === "seller";
    const isBuyer = user_role === "buyer";

    const handleConfirm = () => {
        if (confirmAction) {
            onAction(confirmAction.type);
            setConfirmAction(null);
        }
    }; 

    const renderSellerActions = () => {
        switch (status) {
            case "requested":
                return (
                    <div className="order-actions__buttons">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onAction("send-invoice")}
                            disabled={loading}
                            loading={loading}
                        >
                            Send Invoice
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setConfirmAction({
                                type: "deny",
                                title: "Deny Order",
                                message: "Are you sure you want to deny this order? This action cannot be undone.",
                            })}
                            disabled={loading}
                        >
                            Deny
                        </Button>
                    </div>
                );

            case "invoice_sent":
                return (
                    <div className="order-actions__buttons">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setConfirmAction({
                                type: "confirm-payment",
                                title: "Confirm Payment",
                                message: "Confirm that you have received payment for this order?",
                                variant: "primary",
                            })}
                            disabled={loading}
                            loading={loading}
                        >
                            Confirm Payment
                        </Button>
                    </div>
                );

            case "paid":
            case "handoff_partially_confirmed":
                if (order.fulfillment_method === "buyer_pickup" || order.fulfillment_method === "seller_delivery") {
                    return (
                        <div className="order-actions__waiting">
                            <span className="order-actions__waiting-text">Waiting for buyer to confirm receipt</span>
                        </div>
                    );
                }
                return (
                    <div className="order-actions__buttons">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onAction("ship")}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark Shipped
                        </Button>
                    </div>
                );

            case "shipped":
                return (
                    <div className="order-actions__waiting">
                        <span className="order-actions__waiting-text">
                            Waiting for buyer to confirm receipt
                        </span>
                    </div>
                );

            case "completed":
                return (
                    <div className="order-actions__completed">
                        <span className="order-actions__completed-chip">Completed</span>
                    </div>
                );

            default:
                return null;
        }
    };

    const renderBuyerActions = () => {
        switch (status) {
            case "requested":
                return (
                    <div className="order-actions__buttons">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setConfirmAction({
                                type: "cancel",
                                title: "Cancel Request",
                                message: "Are you sure you want to cancel this order request?",
                            })}
                            disabled={loading}
                        >
                            Cancel Request
                        </Button>
                    </div>
                );

            case "invoice_sent":
                return (
                    <div className="order-actions__buttons">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setConfirmAction({
                                type: "notify-payment",
                                title: "Notify Payment",
                                message: "Let the seller know you have sent payment? This does not confirm the payment — the seller will verify and confirm.",
                                variant: "primary",
                            })}
                            disabled={loading}
                            loading={loading}
                        >
                            I've Paid
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setConfirmAction({
                                type: "cancel",
                                title: "Cancel Order",
                                message: "Are you sure you want to cancel this order?",
                            })}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </div>
                );

            case "paid":
            case "handoff_partially_confirmed": {
                if (order.fulfillment_method === "buyer_pickup" || order.fulfillment_method === "seller_delivery") {
                    const isPickup = order.fulfillment_method === "buyer_pickup";
                    return (
                        <div className="order-actions__buttons">
                            <Button variant="primary" size="sm" onClick={() => onAction("receive")} disabled={loading} loading={loading}>
                                Confirm {isPickup ? "Pickup" : "Delivery"}
                            </Button>
                        </div>
                    );
                }
                return (
                    <div className="order-actions__waiting">
                        <span className="order-actions__waiting-text">
                            {order.fulfillment_method === "seller_delivery"
                                ? "Personal delivery arranged"
                                : "Waiting for seller to choose fulfillment"}
                        </span>
                    </div>
                );
            }

            case "shipped":
                return (
                    <div className="order-actions__buttons">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onAction("receive")}
                            disabled={loading}
                            loading={loading}
                        >
                            Confirm Receipt
                        </Button>
                    </div>
                );

            case "completed":
                return (
                    <div className="order-actions__completed">
                        <span className="order-actions__completed-chip">Completed</span>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="order-actions">
            {isSeller && renderSellerActions()}
            {isBuyer && renderBuyerActions()}

            {confirmAction && (
                <ConfirmModal
                    title={confirmAction.title}
                    message={confirmAction.message}
                    confirmLabel="Confirm"
                    variant={confirmAction.variant || "danger"}
                    onConfirm={handleConfirm}
                    onCancel={() => setConfirmAction(null)}
                />
            )}
        </div>
    );
};

export default OrderActions;
